import { motion } from 'framer-motion'
import { HiSparkles, HiChartBar, HiLightningBolt, HiEye, HiPlay } from 'react-icons/hi'

interface HeroSectionProps {
  onCTAClick: () => void
}

export function HeroSection({ onCTAClick }: HeroSectionProps) {
  return (
    <section className="hero-section">
      {/* Animated background */}
      <div className="hero-background">
        <div className="hero-grid" />
        <div className="hero-orb hero-orb-1" />
        <div className="hero-orb hero-orb-2" />
        <div className="hero-orb hero-orb-3" />
      </div>

      <div className="hero-content">
        <motion.div
          className="hero-badge"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <HiSparkles className="hero-badge-icon" />
          <span>Powered by Dixon-Coles ML Model</span>
        </motion.div>

        <motion.h1
          className="hero-headline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Bet Smarter,<br />
          <span className="hero-headline-gradient">Not Harder</span>
        </motion.h1>

        <motion.p
          className="hero-subheadline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          The analytics platform that shows you <strong>why</strong>, not just what to bet.
          Data-driven insights across 60+ markets, 6 leagues, and 31 bookmakers.
        </motion.p>

        <motion.div
          className="hero-pillars"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <motion.div
            className="hero-pillar"
            whileHover={{ scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <HiChartBar className="hero-pillar-icon" style={{ color: '#7c5cff' }} />
            <h3 className="hero-pillar-title">AI-Powered Builder</h3>
            <p className="hero-pillar-desc">
              60+ betting markets with intelligent suggestions. Per-90 stats, position-aware player props, and ML-driven probabilities.
            </p>
          </motion.div>

          <motion.div
            className="hero-pillar"
            whileHover={{ scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <HiEye className="hero-pillar-icon" style={{ color: '#4ade80' }} />
            <h3 className="hero-pillar-title">Transparent Analytics</h3>
            <p className="hero-pillar-desc">
              No tips, just data. See exactly why each line is suggested with sample sizes, referee impact, and confidence levels.
            </p>
          </motion.div>

          <motion.div
            className="hero-pillar"
            whileHover={{ scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <HiLightningBolt className="hero-pillar-icon" style={{ color: '#fbbf24' }} />
            <h3 className="hero-pillar-title">Real-Time Intelligence</h3>
            <p className="hero-pillar-desc">
              Live stats every 1-2 minutes. Predicted lineups, head-to-head analysis, and player comparisons across 6 major leagues.
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-cta-group"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="hero-cta-row">
            <button className="cta-button large pulse" onClick={onCTAClick}>
              Start Your 7-Day Free Trial
            </button>
            <button className="cta-button-ghost" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
              <HiPlay style={{ fontSize: '1.2rem' }} />
              See How It Works
            </button>
          </div>
          <p className="hero-cta-subtext">
            Then just £7.95/month. Cancel anytime.
          </p>

          {/* Social proof */}
          <div className="hero-social-proof">
            <div className="hero-avatars">
              <div className="hero-avatar">JM</div>
              <div className="hero-avatar">SK</div>
              <div className="hero-avatar">TC</div>
              <div className="hero-avatar">+</div>
            </div>
            <span className="hero-social-text">
              <strong>500+</strong> bettors already using StatScout
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
